<template>
  <div class="p-4">
    <div class="max-w-sm mx-auto text-center">
      <logo class="mt-2 mb-3" />
      <h1 class="text-3xl dark:text-white font-bold mb-5">GitHub Release Stats</h1>
      <div class="bg-white dark:bg-gray-700 py-6 px-4 rounded shadow-xl">
        <form @submit.prevent="getStats">
          <input type="text" placeholder="Repository owner" autocapitalize="none" required
            v-model="repoOwner">
          <input type="text" placeholder="Repository name" autocapitalize="none" required
            v-model="repoName">
          <block-button :loading="loading">Fetch data</block-button>
        </form>
      </div>
      <div class="text-center my-3">
        <tool-button icon="adjust" @click="toggleDarkTheme()"
          :title="`Switch to ${isDarkTheme ? 'light' : 'dark'} theme`" aria-hidden="true" />
      </div>
      <div v-if="error"
        class="mx-auto bg-orange-light font-semibold p-4 my-4 border-l-2 border-orange shadow-lg">
          {{ error }}
      </div>
    </div>
    <main v-if="releases.length">
      <div class="text-center dark:text-white my-5">
        <p>Total Downloads:</p>
        <p class="text-2xl font-semibold mb-4">{{ formatNumber(downloads) }}</p>
      </div>
      <div class="text-center mb-3">
        <tool-button title="Switch to asset list" icon="th-list"
          :active="view === Views.List" @click="setView(Views.List)" />
        <tool-button title="Switch to release table" icon="th"
          :active="view === Views.Table" @click="setView(Views.Table)" />
      </div>
      <release-list :releases="releases" v-if="view === Views.List" />
      <release-table :releases="releases" v-if="view === Views.Table" />
    </main>
    <footer v-else class="max-w-xl mx-auto text-gray-500 text-sm mt-10">
      <p>
        <b>GitHub Release Stats</b> is a small web application
        that helps you to easily fetch the download count
        and other statistics for GitHub repository releases.
      </p>
      <p>
        You can also check out the
        <a href="https://github.com/kefir500/github-release-stats" target="_blank">Node.js</a> and
        <a href="https://github.com/kefir500/ghstats" target="_blank">Python</a>
        implementations of this functionality. Both of these packages can be used
        as a command-line tool and a module.
      </p>
      <p>
        Created by <a href="https://github.com/kefir500" target="_blank">Alexander Gorishnyak</a>.
        This project is <strong>not</strong> affiliated with GitHub, Inc.
      </p>
    </footer>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import { useStore, mapMutations, mapActions } from 'vuex';
import { GithubStats } from 'github-release-stats';
import BlockButton from './components/BlockButton.vue';
import Logo from './components/Logo.vue';
import ReleaseList from './components/ReleaseList.vue';
import ReleaseTable from './components/ReleaseTable.vue';
import ToolButton from './components/ToolButton.vue';
import { Views } from './utilities/enums';
import { formatNumber } from './utilities/filters';

export default {
  components: {
    BlockButton,
    Logo,
    ReleaseList,
    ReleaseTable,
    ToolButton,
  },
  setup() {
    const store = useStore();
    store.dispatch('init');

    const repoOwner = ref('');
    const repoName = ref('');
    const releases = ref([]);

    const loading = ref(false);
    const error = ref('');
    const downloads = computed(() => releases.value.reduce(
      (acc1, release) => acc1 + release.assets.reduce(
        (acc2, asset) => acc2 + asset.download_count, 0,
      ), 0,
    ));

    const view = computed(() => store.state.view);
    const isDarkTheme = computed(() => store.state.darkTheme);

    const getStats = async () => {
      const gh = new GithubStats(repoOwner.value.trim(), repoName.value.trim());
      releases.value = [];
      loading.value = true;
      error.value = false;
      try {
        releases.value = await gh.getAllReleases();
      } catch (err) {
        error.value = err;
      }
      loading.value = false;
    };

    return {
      repoOwner,
      repoName,
      releases,
      loading,
      error,
      downloads,
      view,
      Views,
      isDarkTheme,
      getStats,
      formatNumber,
      ...mapMutations(['setView']),
      ...mapActions(['toggleDarkTheme']),
    };
  },
};
</script>
